import { useCallback, useEffect } from "react";
import type React from "react";
import { dialogTitleStyle } from "./destructiveStyles";

export type UnsavedAction = "save" | "discard" | "cancel";

interface UnsavedChangesDialogProps {
  /** Whether the current drawing is file-backed (has driveId/itemId). */
  isFileBacked: boolean;
  /** Called when the user picks an action. */
  onAction: (action: UnsavedAction) => void;
  /** Whether a save is currently in progress. */
  saving?: boolean;
}

/**
 * Modal dialog shown when the user tries to navigate away (Home / New Drawing)
 * with unsaved changes. Styled consistently with FeedbackDialog / CollabShareDialog.
 *
 * For scene-only drawings (not file-backed), the "Save" option is hidden because
 * there is no file to save to — the user is warned that changes will be lost.
 */
export default function UnsavedChangesDialog({
  isFileBacked,
  onAction,
  saving = false,
}: UnsavedChangesDialogProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onAction("cancel");
    },
    [onAction],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [handleKeyDown]);

  return (
    <div style={overlayStyle} onClick={() => onAction("cancel")}>
      <div
        style={dialogStyle}
        onClick={(e) => e.stopPropagation()}
        data-testid="unsaved-changes-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="unsaved-changes-title"
      >
        <h2 style={dialogTitleStyle} id="unsaved-changes-title">
          Unsaved Changes
        </h2>
        <div style={bodyStyle}>
          <p style={messageStyle}>
            {isFileBacked
              ? "You have unsaved changes. Would you like to save before leaving?"
              : "You have unsaved changes that will be lost."}
          </p>
          <div style={buttonRowStyle}>
            <button
              style={dangerButtonStyle}
              onClick={() => onAction("discard")}
              data-testid="unsaved-discard-btn"
            >
              Discard & Leave
            </button>
            {isFileBacked && (
              <button
                style={primaryButtonStyle}
                onClick={() => onAction("save")}
                disabled={saving}
                data-testid="unsaved-save-btn"
              >
                {saving ? "Saving…" : "Save & Leave"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles — consistent with FeedbackDialog / CollabShareDialog
// ---------------------------------------------------------------------------

const EXCALIDRAW_FONT =
  "Assistant, system-ui, BlinkMacSystemFont, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif";

const MODAL_SHADOW = "var(--exms-shadow-modal)";

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 10001,
  background: "var(--exms-overlay-bg)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
};

const dialogStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 560,
  boxSizing: "border-box",
  background: "var(--island-bg-color)",
  border: "1px solid var(--dialog-border-color)",
  borderRadius: 12,
  boxShadow: MODAL_SHADOW,
  fontFamily: EXCALIDRAW_FONT,
  padding: 24,
};

const bodyStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 16,
  marginTop: 20,
};

const messageStyle: React.CSSProperties = { margin: 0, fontSize: 15, lineHeight: 1.5 };

const buttonRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  flexWrap: "wrap",
  gap: 12,
};

const secondaryButtonStyle: React.CSSProperties = {
  borderRadius: 10,
  border: "1px solid var(--dialog-border-color)",
  padding: "12px 20px",
  cursor: "pointer",
  fontSize: 15,
  fontFamily: "inherit",
  fontWeight: 700,
  whiteSpace: "nowrap",
  background: "var(--island-bg-color)",
  color: "inherit",
};

const dangerButtonStyle: React.CSSProperties = {
  ...secondaryButtonStyle,
  color: "var(--color-on-accent)",
  background: "var(--color-danger)",
  borderColor: "var(--color-danger)",
};

const primaryButtonStyle: React.CSSProperties = {
  ...secondaryButtonStyle,
  color: "var(--color-on-accent)",
  background: "var(--color-primary)",
  borderColor: "var(--color-primary)",
};
